import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, formatApiError, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectGroup, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { ChevronLeft, Save, Zap, GripVertical, FileDown, Send, ChevronsUpDown, Check, Plus, Trash2, History, CircleDot, UserCheck, Receipt, PlayCircle, CheckCircle2, PauseCircle, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import FileUploader from "@/components/FileUploader";
import RepeatJobDialog from "@/components/RepeatJobDialog";

const SERVICE_OPTIONS = ["Same-day", "Next-day", "Multi-drop", "Overnight", "Scheduled", "DSP"];
const STATUS_OPTIONS = [
  { value: "new", label: "New" },
  { value: "assigned", label: "Assigned" },
  { value: "in_progress", label: "In Progress" },
  { value: "complete", label: "Complete" },
  { value: "on_hold", label: "On Hold" },
  { value: "cancelled", label: "Cancelled" },
];
const PRIORITY_OPTIONS = [
  { value: "low", label: "Low" },
  { value: "normal", label: "Normal" },
  { value: "high", label: "High" },
  { value: "urgent", label: "Urgent" },
];

const DSP_VEHICLE_TYPES = [
  "Small van",
  "Medium van (SWB)",
  "Large van (LWB)",
  "Luton van",
  "7.5 tonne",
  "18 tonne",
  "Car",
  "Motorbike / cycle",
  "Mixed fleet",
];

const today = () => new Date().toISOString().slice(0, 10);

const EMPTY = {
  date: today(),
  due_date: "",
  priority: "normal",
  customer_id: "",
  customer_snapshot: { name: "" },
  client_id: "",
  site_id: "",
  po_number: "",
  code: "",
  subcontractor_id: "",
  subcontractor_override: false,
  driver_id: "",
  driver_snapshot: { name: "", driver_id: "" },
  assignee_other: "",
  service_type: "Same-day",
  dsp_driver_count: 0,
  dsp_days: [],
  dsp_vehicle_type: "",
  dsp_period: "weekly",
  end_date: "",
  description: "",
  collection_address: "",
  collection_time: "",
  dropoff_address: "",
  dropoff_time: "",
  extra_dropoffs: [],
  extra_routes: [],
  extra_dates: [],
  package_description: "",
  package_weight: 0,
  package_pieces: 1,
  vehicle_reg: "",
  distance_miles: 0,
  charge: 0,
  driver_rate: 0,
  status: "new",
  instructions: "",
  notes: "",
};

// Parse "YYYY-MM-DD" as a local-midnight Date (avoids UTC drift).
function parseISO(iso) {
  if (!iso) return null;
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d);
}
// Format a Date back to "YYYY-MM-DD" using its LOCAL components (NOT toISOString).
function toISO(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// Build/sync the dsp_days array from a start..end range, preserving existing entries.
// Each entry: {date, site_id, drivers}. Multiple entries per day are allowed.
function buildDspDays(startISO, endISO, existing = []) {
  if (!startISO || !endISO) return [];
  const start = parseISO(startISO);
  const end = parseISO(endISO);
  if (end < start) return existing;
  // collect valid dates in range
  const validDates = new Set();
  const cur = new Date(start);
  while (cur <= end) {
    validDates.add(toISO(cur));
    cur.setDate(cur.getDate() + 1);
    if (validDates.size > 90) break;
  }
  // keep existing entries whose date still falls in range
  const kept = existing.filter((e) => validDates.has(e.date));
  // ensure at least one entry per date
  const datesWithEntries = new Set(kept.map((e) => e.date));
  validDates.forEach((d) => {
    if (!datesWithEntries.has(d)) {
      kept.push({ date: d, site_id: "", subcontractor_id: "", drivers: 0 });
    }
  });
  // sort by date
  kept.sort((a, b) => a.date.localeCompare(b.date));
  return kept;
}
const WEEKDAY_LABEL = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
function weekdayLabel(iso) {
  const d = parseISO(iso);
  return WEEKDAY_LABEL[(d.getDay() + 6) % 7];
}
// Snap any ISO date to the Monday of its week (Mon-Sun week).
function snapToMonday(iso) {
  if (!iso) return iso;
  const d = parseISO(iso);
  // JS: Sun=0..Sat=6; offset to Mon=0..Sun=6
  const offset = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - offset);
  return toISO(d);
}
// Compute the Sunday for a given Monday-ISO (Monday + 6 days).
function sundayFromMonday(mondayISO) {
  if (!mondayISO) return "";
  const d = parseISO(mondayISO);
  d.setDate(d.getDate() + 6);
  return toISO(d);
}
// Snap any ISO date to the 1st of its month.
function firstOfMonth(iso) {
  if (!iso) return iso;
  const d = parseISO(iso);
  d.setDate(1);
  return toISO(d);
}
// Last calendar day of the month containing this ISO date.
function lastOfMonth(iso) {
  if (!iso) return "";
  const d = parseISO(iso);
  d.setMonth(d.getMonth() + 1);
  d.setDate(0); // last day of previous month i.e. original
  return toISO(d);
}

export default function JobForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [number, setNumber] = useState("");
  const [drivers, setDrivers] = useState([]);
  const [subs, setSubs] = useState([]);
  const [clients, setClients] = useState([]);
  const [sites, setSites] = useState([]);
  const [routedSource, setRoutedSource] = useState(null); // 'specific' | 'all' | 'default' | null
  const [saving, setSaving] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const [chargeBySite, setChargeBySite] = useState({}); // site_id -> {charge_rate, unit, label}
  // Explicit assignment mode — stored in state so clicking "Other" sticks even
  // when the free-text input hasn't been filled in yet.
  const [assignMode, setAssignModeState] = useState("sub");

  // initial loads
  useEffect(() => {
    api.get("/contacts", { params: { type: "subcontractor" } }).then((r) => setDrivers(r.data));
    api.get("/contacts", { params: { type: "customer" } }).then((r) => setSubs(r.data));
    api.get("/clients").then((r) => setClients(r.data));
    // Preload the charge-rate card so we can auto-fill the "Charge to customer" field
    // the moment a client + site are picked. Silent failure — the field just stays blank.
    api.get("/finance/chargeable-rates").then((r) => {
      const map = {};
      for (const g of (r.data.clients || [])) {
        for (const rate of (g.rates || [])) {
          if (rate.site_id && !map[rate.site_id]) {
            map[rate.site_id] = { charge_rate: rate.charge_rate, unit: rate.unit, label: rate.label };
          }
        }
      }
      setChargeBySite(map);
    }).catch(() => {});
  }, []);

  // load job for edit
  useEffect(() => {
    if (!isEdit) return;
    api.get(`/jobs/${id}`).then((r) => {
      const data = r.data;
      setForm({ ...EMPTY, ...data, date: (data.date || "").slice(0, 10) });
      setNumber(data.number);
      // Sync the mode toggle so editing a job lands on the right tab
      setAssignModeState(
        data.assignee_other ? "other" : (data.driver_id ? "self" : "sub")
      );
    });
  }, [id, isEdit]);

  // when client changes, load its sites
  useEffect(() => {
    if (!form.client_id) { setSites([]); return; }
    api.get(`/clients/${form.client_id}/sites`).then((r) => setSites(r.data));
  }, [form.client_id]);

  // Auto-fill the primary Collection address from the client's per-service default
  // (set on the Client form under "Services used"). Never overwrites what the user
  // has already typed — only fills when the field is empty.
  useEffect(() => {
    if (isEdit) return;                        // don't touch existing jobs
    if (!form.client_id || !form.service_type) return;
    if (form.collection_address) return;       // already has something
    const client = clients.find((c) => c.id === form.client_id);
    const addr = client?.service_collection_addresses?.[form.service_type];
    if (addr) {
      setForm((f) => ({ ...f, collection_address: addr }));
      toast.success(`Collection prefilled from ${client.name}'s ${form.service_type} default`);
    }
  }, [form.client_id, form.service_type, clients, isEdit]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onClientSelect = (val) => {
    if (val === "__none") {
      setForm((f) => ({ ...f, client_id: "", site_id: "", subcontractor_id: "", subcontractor_override: false, customer_snapshot: { name: "" } }));
      setRoutedSource(null);
      return;
    }
    const c = clients.find((x) => x.id === val);
    setForm((f) => ({ ...f, client_id: val, site_id: "", subcontractor_id: "", subcontractor_override: false, customer_snapshot: { name: c?.name || "" } }));
    setRoutedSource(null);
  };

  const onSiteSelect = async (val) => {
    if (val === "__none") { set("site_id", ""); set("subcontractor_id", ""); set("subcontractor_override", false); setRoutedSource(null); return; }
    const s = sites.find((x) => x.id === val);
    // Auto-fill "Charge to customer" from the Chargeable rate card if we have one
    // and the user hasn't manually typed a charge yet. Also copy the site's saved
    // total miles into distance_miles so per-mile charges/pays calculate correctly.
    const rate = chargeBySite[val];
    // Pull the site's saved drops (each just a dropoff_address). When set,
    // they become the job's drop-offs — Drop 1 → primary Drop-off, Drop 2+ → extras.
    const siteDrops = (s?.routes || []).filter((r) => (r?.dropoff_address || "").trim());
    const shouldFillDropoffs = siteDrops.length > 0 && !form.dropoff_address && (form.extra_dropoffs || []).length === 0;
    setForm((f) => ({
      ...f,
      site_id: val,
      collection_address: f.collection_address ||
        [s?.address_line1, s?.address_line2, s?.city, s?.postcode].filter(Boolean).join(", "),
      dropoff_address: shouldFillDropoffs ? siteDrops[0].dropoff_address : f.dropoff_address,
      extra_dropoffs: shouldFillDropoffs
        ? siteDrops.slice(1).map((r) => ({ address: r.dropoff_address, time: "" }))
        : f.extra_dropoffs,
      charge: (!f.charge || Number(f.charge) === 0) && rate ? rate.charge_rate : f.charge,
      distance_miles:
        (!f.distance_miles || Number(f.distance_miles) === 0)
          ? (s?.miles || f.distance_miles)
          : f.distance_miles,
    }));
    if (shouldFillDropoffs) {
      toast.success(`${siteDrops.length} drop${siteDrops.length === 1 ? "" : "s"} auto-filled from site`);
    }
    if (rate && (!form.charge || Number(form.charge) === 0)) {
      const unitLabel = { per_job: "per job", per_day: "per day", per_hour: "per hour", per_week: "per week", per_mile: "per mile" }[rate.unit] || rate.unit;
      toast.success(`Charge prefilled: £${rate.charge_rate.toFixed(2)} ${unitLabel}`);
    }
    // auto-resolve subcontractor
    try {
      const r = await api.get(`/sites/${val}/resolve-subcontractor`);
      if (r.data.subcontractor_id && !form.subcontractor_override) {
        set("subcontractor_id", r.data.subcontractor_id);
        setRoutedSource(r.data.source);
        toast.success(`Auto-routed to ${r.data.subcontractor?.name || "subcontractor"} (${r.data.source})`);
      } else if (!r.data.subcontractor_id) {
        setRoutedSource(null);
        toast.info("No subcontractor assigned for this site. Pick one manually below.");
      }
    } catch (e) { /* silent */ }
  };

  const onOverrideToggle = (v) => {
    set("subcontractor_override", v);
    if (!v && form.site_id) {
      // turning OFF override -> re-resolve
      onSiteSelect(form.site_id);
    }
  };

  const onDriverSelect = (val) => {
    if (val === "__none") { set("driver_id", ""); set("driver_snapshot", { name: "", driver_id: "" }); return; }
    const d = drivers.find((x) => x.id === val);
    if (d) {
      setForm((f) => ({
        ...f,
        driver_id: d.id,
        driver_snapshot: { name: d.name, driver_id: d.driver_id || "" },
        // Mutually exclusive: clear the subcontractor when picking a driver
        subcontractor_id: "",
        subcontractor_override: true,
        ...(d.uses_company_vehicle && d.vehicle_reg && !f.vehicle_reg ? { vehicle_reg: d.vehicle_reg } : {}),
        ...(f.status === "new" ? { status: "assigned" } : {}),
      }));
      setRoutedSource(null);
    }
  };

  const submit = async () => {
    const selectedClient = clients.find((c) => c.id === form.client_id);
    if (selectedClient?.po_required_for_invoice && !(form.po_number || "").trim()) {
      return toast.error(`${selectedClient.name} requires a PO number on every job.`);
    }
    if (form.service_type !== "DSP" && (!form.collection_address || !form.dropoff_address)) {
      return toast.error("Please enter both collection and drop-off addresses.");
    }
    if (form.service_type === "DSP") {
      if (!form.date || !form.end_date) return toast.error("Pick the week start and end dates.");
      if ((form.dsp_days || []).length === 0 || (form.dsp_days || []).every((d) => !Number(d.drivers))) {
        return toast.error("Please enter how many drivers were sent each day.");
      }
    }
    const totalDsp = (form.dsp_days || []).reduce((s, d) => s + (Number(d.drivers) || 0), 0);
    // Multi-route works for any non-DSP job; drop empty rows so the record stays clean.
    // Also clean each route's own extra_dropoffs (per-route multi-drop) — same rule
    // as the primary drop-offs: keep rows with a non-empty address.
    const cleanedExtras = form.service_type !== "DSP"
      ? (form.extra_routes || [])
          .filter((r) => (r.collection_address || "").trim() || (r.dropoff_address || "").trim())
          .map((r) => ({
            ...r,
            extra_dropoffs: (r.extra_dropoffs || []).filter((d) => (d.address || "").trim()),
          }))
      : [];
    const cleanedDropoffs = form.service_type !== "DSP"
      ? (form.extra_dropoffs || []).filter((d) => (d.address || "").trim())
      : [];
    const payload = {
      ...form,
      package_weight: Number(form.package_weight) || 0,
      package_pieces: Number(form.package_pieces) || 1,
      distance_miles: Number(form.distance_miles) || 0,
      charge: Number(form.charge) || 0,
      driver_rate: Number(form.driver_rate) || 0,
      dsp_driver_count: form.service_type === "DSP" ? totalDsp : 0,
      dsp_days: form.service_type === "DSP" ? form.dsp_days : [],
      end_date: form.service_type === "DSP" ? (form.end_date || null) : null,
      due_date: form.service_type === "DSP" ? null : (form.due_date || null),
      extra_routes: cleanedExtras,
      extra_dropoffs: cleanedDropoffs,
    };
    // "Also create on these dates" — only supported for new, non-DSP jobs.
    const cloneDates = (!isEdit && form.service_type !== "DSP")
      ? Array.from(new Set((form.extra_dates || []).filter((d) => (d || "").trim() && d !== form.date)))
      : [];
    // Strip our UI-only key before sending to the API.
    delete payload.extra_dates;
    setSaving(true);
    try {
      const res = isEdit ? await api.put(`/jobs/${id}`, payload) : await api.post("/jobs", payload);
      if (cloneDates.length > 0) {
        // Clone the job onto each additional date. Each clone is a fresh job
        // with status=new so ops can edit them independently.
        const clonePayload = { ...payload, status: "new" };
        const results = await Promise.allSettled(
          cloneDates.map((d) => api.post("/jobs", { ...clonePayload, date: d }))
        );
        const ok = results.filter((r) => r.status === "fulfilled").length;
        const fail = results.length - ok;
        if (fail === 0) {
          toast.success(`${res.data.number} + ${ok} more date${ok === 1 ? "" : "s"} created`);
        } else {
          toast.warning(`${res.data.number} saved. ${ok} extra date${ok === 1 ? "" : "s"} created, ${fail} failed.`);
        }
        navigate("/jobs");
        return;
      }
      toast.success(`${res.data.number} saved`);
      navigate(`/jobs/${res.data.id}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const subName = (sid) => subs.find((x) => x.id === sid)?.name || "";
  // Inactive subs/drivers are hidden from pickers, unless already assigned on this job.
  const assignedIds = new Set([form.subcontractor_id, ...(form.dsp_days || []).map((d) => d.subcontractor_id)].filter(Boolean));
  const pickSubs = subs.filter((x) => x.is_active !== false || assignedIds.has(x.id));
  const pickDrivers = drivers.filter((x) => x.is_active !== false || assignedIds.has(x.id));

  // Assignment card — defined here so it can be rendered in different positions
  // for DSP (top of form) vs Same-day (right after the Route card).
  const setAssignMode = (m) => {
    setAssignModeState(m);
    if (m === "sub") {
      setForm((f) => ({ ...f, assignee_other: "", driver_id: "", driver_snapshot: { name: "", driver_id: "" } }));
    } else if (m === "self") {
      setForm((f) => ({ ...f, assignee_other: "", subcontractor_id: "", subcontractor_override: true }));
      setRoutedSource(null);
    } else {
      setForm((f) => ({ ...f, subcontractor_id: "", subcontractor_override: true, driver_id: "", driver_snapshot: { name: "", driver_id: "" } }));
      setRoutedSource(null);
    }
  };
  const AssignmentCard = (
    <Card className="rounded-sm border border-slate-200 shadow-none p-6" data-testid="assignment-card">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Assignment</div>
        <div className="flex items-center gap-2">
          {routedSource && !form.subcontractor_override && (
            <Badge variant="secondary" className="rounded-sm inline-flex items-center gap-1">
              <Zap className="h-3 w-3" /> Auto-routed ({routedSource})
            </Badge>
          )}
          <div className="inline-flex items-center gap-1 bg-slate-100 rounded-sm p-1" data-testid="assign-mode-toggle">
            {[["sub", "Subcontractor"], ["self", "Self-employed"], ["other", "Other"]].map(([key, label]) => (
              <button
                key={key}
                type="button"
                data-testid={`assign-mode-${key}`}
                onClick={() => setAssignMode(key)}
                className={`px-3 py-1.5 text-xs font-medium rounded-sm transition-colors ${
                  assignMode === key
                    ? "bg-slate-900 text-white"
                    : "bg-white text-slate-700 hover:bg-slate-100 border border-slate-200"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>
      {assignMode === "sub" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs text-slate-600">Subcontractor (company)</Label>
            <Select
              value={form.subcontractor_id || "__none"}
              onValueChange={(v) => {
                const newSub = v === "__none" ? "" : v;
                setForm((f) => ({
                  ...f,
                  subcontractor_id: newSub,
                  ...(newSub && f.driver_id ? { driver_id: "", driver_snapshot: { name: "", driver_id: "" } } : {}),
                  subcontractor_override: f.subcontractor_override || !!newSub,
                  assignee_other: "",
                }));
                setRoutedSource(null);
              }}
            >
              <SelectTrigger data-testid="job-sub" className="mt-1 rounded-sm border-slate-300">
                <SelectValue placeholder="Pick subcontractor" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">— None —</SelectItem>
                {pickSubs.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}
              </SelectContent>
            </Select>
            {form.subcontractor_id && (
              <div className="text-[11px] text-slate-500 mt-1">Currently: {subName(form.subcontractor_id)}</div>
            )}
          </div>
          <div className="flex items-center justify-between bg-slate-50 rounded-sm p-3 border border-slate-200">
            <div>
              <div className="text-sm font-medium text-slate-900">Manual override</div>
              <div className="text-xs text-slate-500">Bypass subcontractor auto-routing.</div>
            </div>
            <Switch
              data-testid="job-override"
              checked={form.subcontractor_override}
              onCheckedChange={onOverrideToggle}
            />
          </div>
        </div>
      )}
      {assignMode === "self" && (
        <div>
          <Label className="text-xs text-slate-600">Self-employed driver</Label>
          <Select value={form.driver_id || "__none"} onValueChange={onDriverSelect}>
            <SelectTrigger data-testid="job-driver" className="mt-1 rounded-sm border-slate-300">
              <SelectValue placeholder="Pick a self-employed driver" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none">— None —</SelectItem>
              {pickDrivers.map((d) => (
                <SelectItem key={d.id} value={d.id}>
                  {d.name}{d.driver_id ? ` (${d.driver_id})` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {form.driver_id && (
            <div className="text-[11px] text-slate-500 mt-1">Currently: {form.driver_snapshot?.name || ""}</div>
          )}
        </div>
      )}
      {assignMode === "other" && (
        <div>
          <Label className="text-xs text-slate-600">Assignee name</Label>
          <Input
            data-testid="job-assignee-other"
            value={form.assignee_other || ""}
            onChange={(e) => set("assignee_other", e.target.value)}
            placeholder="e.g. John Casual (one-off driver)"
            className="mt-1 rounded-sm border-slate-300"
          />
          <p className="text-[11px] text-slate-500 mt-1">
            Use this for one-off drivers who aren&apos;t in your Subs or Self-Employed lists.
          </p>
        </div>
      )}
    </Card>
  );

  return (
    <div data-testid="job-form-page" className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <Link to="/jobs" className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
            <ChevronLeft className="h-3 w-3" /> Back to Jobs
          </Link>
          <h1 className="font-display text-3xl tracking-tight font-semibold text-slate-900 mt-2">
            {isEdit ? `Job ${number}` : "New Job"}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          {isEdit && (
            <a
              href={fileUrl(`/jobs/${id}/pdf`)}
              download
              rel="noopener"
              data-testid="job-form-pdf-btn"
              className="inline-flex items-center gap-2 h-9 px-3 rounded-sm border border-slate-200 text-sm text-slate-700 hover:bg-slate-50"
            >
              <FileDown className="h-4 w-4" /> Download PDF
            </a>
          )}
          {isEdit && (
            <a
              href={fileUrl(`/jobs/${id}/client-pdf`)}
              download
              rel="noopener"
              data-testid="job-form-client-pdf-btn"
              title="Download client-ready Delivery Note (no supplier info, no pricing)"
              className="inline-flex items-center gap-2 h-9 px-3 rounded-sm border border-emerald-200 bg-emerald-50 text-sm text-emerald-800 hover:bg-emerald-100"
            >
              <Send className="h-4 w-4" /> Client PDF
            </a>
          )}
          {isEdit && (
            <RepeatJobDialog
              jobId={id}
              defaultStart={form.date}
              triggerTestId="job-form-repeat-btn"
              onCreated={() => navigate("/jobs")}
            />
          )}
          <Button data-testid="job-save-btn" onClick={submit} disabled={saving} className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white">
            <Save className="h-4 w-4 mr-2" /> {isEdit ? "Update" : "Create"}
          </Button>
        </div>
      </div>

      {/* Client */}
      <Card className="rounded-sm border border-slate-200 shadow-none p-6">
        <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold mb-4">Client</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs text-slate-600">Client</Label>
            <SearchableCombo
              testid="job-client"
              placeholder="Type to search a client…"
              value={form.client_id || ""}
              onChange={(v) => onClientSelect(v || "__none")}
              options={[
                { value: "", label: "— Type manually below —" },
                ...clients.map((c) => ({ value: c.id, label: c.name })),
              ]}
            />
            {clients.length === 0 && (
              <div className="text-xs text-amber-600 mt-1">
                No clients yet. <Link to="/clients" className="underline">Add one</Link> to use auto-routing.
              </div>
            )}
          </div>
          <div>
            <Label className="text-xs text-slate-600">
              Customer name (if not picking a client)
            </Label>
            <Input data-testid="job-customer-manual" value={form.customer_snapshot?.name || ""}
              onChange={(e) => set("customer_snapshot", { ...form.customer_snapshot, name: e.target.value })}
              placeholder="Free-text customer name (for one-off jobs)" className="mt-1 rounded-sm border-slate-300" />
          </div>
          <div>
            <Label className="text-xs text-slate-600">Code</Label>
            <Input data-testid="job-code" value={form.code || ""}
              onChange={(e) => set("code", e.target.value)}
              placeholder="Free-text job code" className="mt-1 rounded-sm border-slate-300" />
          </div>
          {(() => {
            const selectedClient = clients.find((c) => c.id === form.client_id);
            if (!selectedClient?.po_required_for_invoice) return null;
            return (
              <div className="md:col-span-2 rounded-sm border border-amber-200 bg-amber-50 px-4 py-3">
                <Label className="text-xs font-semibold text-amber-800 uppercase tracking-[0.14em]">
                  PO Number <span className="text-red-600">*</span>
                </Label>
                <p className="text-[11px] text-amber-700 mb-2">
                  {selectedClient.name} requires a Purchase Order number on every job before it can be invoiced.
                </p>
                <Input
                  data-testid="job-po-number"
                  value={form.po_number || ""}
                  onChange={(e) => set("po_number", e.target.value)}
                  placeholder="e.g. CUL-2026-0142"
                  className="rounded-sm border-amber-300 bg-white"
                />
              </div>
            );
          })()}
        </div>
      </Card>

      {/* Assignment — for DSP jobs, sits at the top just under Client.
          For Same-day / non-DSP it is rendered right after the Route card below. */}
      {form.service_type === "DSP" && AssignmentCard}

      {/* Top: Date + Priority + Status + Service */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className={`rounded-sm border border-slate-200 shadow-none p-6 ${form.service_type === "DSP" ? "lg:col-span-3" : "lg:col-span-2"}`}>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold mb-4">Job details</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {form.service_type !== "DSP" && (
              <Field label="Date raised" testid="job-date" type="date" value={form.date} onChange={(v) => set("date", v)} />
            )}
            {form.service_type !== "DSP" ? (
              <Field label="Due date" testid="job-due-date" type="date" value={form.due_date || ""} onChange={(v) => set("due_date", v)} />
            ) : (
              <div />
            )}
            <div>
              <Label className="text-xs text-slate-600">Service type</Label>
              <Select
                value={form.service_type}
                onValueChange={(v) => {
                  if (v === "DSP") {
                    const monday = snapToMonday(form.date || today());
                    const sunday = sundayFromMonday(monday);
                    setForm((f) => ({
                      ...f,
                      service_type: "DSP",
                      date: monday,
                      end_date: sunday,
                      dsp_days: buildDspDays(monday, sunday, f.dsp_days),
                    }));
                  } else {
                    set("service_type", v);
                  }
                }}
              >
                <SelectTrigger data-testid="job-service" className="mt-1 rounded-sm border-slate-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SERVICE_OPTIONS.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            {form.service_type !== "DSP" && (
              <div>
                <Label className="text-xs text-slate-600">Priority</Label>
                <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
                  <SelectTrigger data-testid="job-priority" className="mt-1 rounded-sm border-slate-300">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITY_OPTIONS.map((p) => (<SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {form.service_type === "DSP" && (
              <>
                <div>
                  <Label className="text-xs text-slate-600">Period</Label>
                  <Select
                    value={form.dsp_period || "weekly"}
                    onValueChange={(v) => {
                      const base = form.date || today();
                      if (v === "monthly") {
                        const start = firstOfMonth(base);
                        const end = lastOfMonth(start);
                        setForm((f) => ({
                          ...f,
                          dsp_period: "monthly",
                          date: start,
                          end_date: end,
                          dsp_days: buildDspDays(start, end, f.dsp_days),
                        }));
                      } else {
                        const monday = snapToMonday(base);
                        const sunday = sundayFromMonday(monday);
                        setForm((f) => ({
                          ...f,
                          dsp_period: "weekly",
                          date: monday,
                          end_date: sunday,
                          dsp_days: buildDspDays(monday, sunday, f.dsp_days),
                        }));
                      }
                    }}
                  >
                    <SelectTrigger data-testid="job-dsp-period" className="mt-1 rounded-sm border-slate-300">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weekly">Weekly (Mon–Sun)</SelectItem>
                      <SelectItem value="monthly">Monthly (calendar month)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-600">
                    {form.dsp_period === "monthly" ? "Month start (1st)" : "Week start (Mon)"}{" "}
                    <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    data-testid="job-dsp-start"
                    type={form.dsp_period === "monthly" ? "month" : "date"}
                    value={form.dsp_period === "monthly" ? (form.date || "").slice(0, 7) : form.date}
                    onChange={(e) => {
                      const picked = e.target.value;
                      if (form.dsp_period === "monthly") {
                        // input type=month gives "YYYY-MM"; build first-of-month
                        const start = picked ? `${picked}-01` : "";
                        const end = lastOfMonth(start);
                        setForm((f) => ({
                          ...f,
                          date: start,
                          end_date: end,
                          dsp_days: buildDspDays(start, end, f.dsp_days),
                        }));
                      } else {
                        const monday = snapToMonday(picked);
                        const sunday = sundayFromMonday(monday);
                        setForm((f) => ({
                          ...f,
                          date: monday,
                          end_date: sunday,
                          dsp_days: buildDspDays(monday, sunday, f.dsp_days),
                        }));
                      }
                    }}
                    className="mt-1 rounded-sm border-slate-300"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">
                    {form.dsp_period === "monthly"
                      ? "Pick a month — grid covers the whole calendar month."
                      : "Any day you pick snaps to that week's Monday."}
                  </p>
                </div>
                <div>
                  <Label className="text-xs text-slate-600">
                    {form.dsp_period === "monthly" ? "Month end (auto)" : "Week end (Sun)"}
                  </Label>
                  <Input
                    data-testid="job-dsp-end"
                    type="date"
                    value={form.end_date || ""}
                    readOnly
                    className="mt-1 rounded-sm border-slate-300 bg-slate-50 text-slate-600 cursor-not-allowed"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">
                    {form.dsp_period === "monthly"
                      ? "Auto-set to the last day of the chosen month."
                      : "Auto-set to the Sunday of the chosen week."}
                  </p>
                </div>
                <div className="md:col-span-2">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-xs text-slate-600">Drivers sent per day & site <span className="text-red-500">*</span></Label>
                    <span className="text-[11px] text-slate-500 tabular-nums">
                      Total this {form.dsp_period === "monthly" ? "month" : "week"}: <span className="font-semibold text-slate-900" data-testid="dsp-total">{(form.dsp_days || []).reduce((s, d) => s + (Number(d.drivers) || 0), 0)}</span>
                    </span>
                  </div>
                  {form.dsp_days.length === 0 ? (
                    <div className="text-xs text-slate-500 bg-slate-50 border border-dashed border-slate-300 rounded-sm p-4 text-center">
                      Pick a {form.dsp_period === "monthly" ? "month" : "week start"} above to capture drivers per day per site.
                    </div>
                  ) : (() => {
                    // Always show ALL days in the picked range, independent of current row entries.
                    const allWeekDates = [];
                    if (form.date && form.end_date) {
                      const s = parseISO(form.date);
                      const e = parseISO(form.end_date);
                      const cur = new Date(s);
                      while (cur <= e) {
                        allWeekDates.push(toISO(cur));
                        cur.setDate(cur.getDate() + 1);
                        if (allWeekDates.length > 90) break;
                      }
                    }
                    return (
                    <div className="border border-slate-200 rounded-sm bg-white">
                      <div className="grid grid-cols-12 gap-2 px-3 py-2 text-[11px] uppercase tracking-wider text-slate-500 bg-slate-50 border-b border-slate-200">
                        <div className="col-span-1"></div>
                        <div className="col-span-2">Date</div>
                        <div className="col-span-3">Site</div>
                        <div className="col-span-3">{form.driver_id ? "Self-employed driver" : "Subcontractor"}</div>
                        <div className="col-span-2 text-right">Drivers</div>
                        <div className="col-span-1"></div>
                      </div>
                      <div>
                        {form.dsp_days.map((entry, idx) => {
                          const prev = form.dsp_days[idx - 1];
                          // Show an orange day-break divider whenever the date changes
                          // from the previous row (and never above the very first row).
                          const newDay = idx > 0 && (prev?.date || "") !== (entry?.date || "");
                          return (
                          <div
                            key={idx}
                            className={`grid grid-cols-12 gap-2 px-3 py-2 items-center transition-colors border-b border-slate-100 ${
                              newDay ? "border-t-2 border-t-orange-400 mt-2 pt-3" : ""
                            } ${dragIdx === idx ? "opacity-40" : ""} ${overIdx === idx && dragIdx !== idx ? "bg-amber-50" : ""}`}
                            data-testid={`dsp-row-${idx}`}
                            draggable
                            onDragStart={(e) => {
                              setDragIdx(idx);
                              e.dataTransfer.effectAllowed = "move";
                            }}
                            onDragOver={(e) => {
                              e.preventDefault();
                              e.dataTransfer.dropEffect = "move";
                              if (overIdx !== idx) setOverIdx(idx);
                            }}
                            onDragLeave={() => {
                              if (overIdx === idx) setOverIdx(null);
                            }}
                            onDrop={(e) => {
                              e.preventDefault();
                              if (dragIdx === null || dragIdx === idx) {
                                setDragIdx(null); setOverIdx(null); return;
                              }
                              setForm((f) => {
                                const next = [...f.dsp_days];
                                const [moved] = next.splice(dragIdx, 1);
                                next.splice(idx, 0, moved);
                                return { ...f, dsp_days: next };
                              });
                              setDragIdx(null); setOverIdx(null);
                            }}
                            onDragEnd={() => { setDragIdx(null); setOverIdx(null); }}
                          >
                            <div className="col-span-1 flex items-center text-slate-400 cursor-grab active:cursor-grabbing" title="Drag to reorder" data-testid={`dsp-drag-${idx}`}>
                              <GripVertical className="h-4 w-4" />
                            </div>
                            <div className="col-span-2">
                              <Select
                                value={entry.date}
                                onValueChange={(v) => {
                                  setForm((f) => {
                                    const next = [...f.dsp_days];
                                    next[idx] = { ...next[idx], date: v };
                                    return { ...f, dsp_days: next };
                                  });
                                }}
                              >
                                <SelectTrigger data-testid={`dsp-date-${idx}`} className="rounded-sm border-slate-300 h-9">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {allWeekDates.map((d) => (
                                    <SelectItem key={d} value={d}>
                                      <span className="font-medium">{weekdayLabel(d)}</span>
                                      <span className="ml-2 text-slate-500 tabular-nums">{d}</span>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="col-span-3">
                              <SiteCombobox
                                value={entry.site_id || ""}
                                sites={sites}
                                disabled={!form.client_id}
                                placeholder={form.client_id ? "Choose site" : "Pick a client first"}
                                testid={`dsp-site-${idx}`}
                                onChange={async (siteId) => {
                                  // Apply the site immediately for a snappy UI.
                                  setForm((f) => {
                                    const next = [...f.dsp_days];
                                    next[idx] = { ...next[idx], site_id: siteId };
                                    return { ...f, dsp_days: next };
                                  });
                                  // Then auto-fill the row's subcontractor based on the
                                  // client's assignment rules for this site. The user can
                                  // still override via the Sub dropdown afterwards.
                                  if (!siteId) return;
                                  try {
                                    const r = await api.get(`/sites/${siteId}/resolve-subcontractor`);
                                    const subId = r?.data?.subcontractor_id;
                                    if (!subId) return;
                                    setForm((f) => {
                                      const next = [...f.dsp_days];
                                      // Only replace if the row's site hasn't changed again in the meantime.
                                      if (next[idx]?.site_id === siteId) {
                                        next[idx] = { ...next[idx], subcontractor_id: subId };
                                      }
                                      return { ...f, dsp_days: next };
                                    });
                                  } catch {
                                    // Silent — auto-routing is best-effort; user can pick manually.
                                  }
                                }}
                              />
                            </div>
                            <div className="col-span-3">
                              <Select
                                value={entry.subcontractor_id || "__none"}
                                onValueChange={(v) => {
                                  setForm((f) => {
                                    const next = [...f.dsp_days];
                                    next[idx] = { ...next[idx], subcontractor_id: v === "__none" ? "" : v };
                                    return { ...f, dsp_days: next };
                                  });
                                }}
                              >
                                <SelectTrigger data-testid={`dsp-sub-${idx}`} className="rounded-sm border-slate-300 h-9">
                                  <SelectValue placeholder="Pick subcontractor or driver" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="__none">— None —</SelectItem>
                                  {pickSubs.length > 0 && (
                                    <SelectGroup>
                                      <SelectLabel className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Subcontractors</SelectLabel>
                                      {pickSubs.map((s) => (
                                        <SelectItem key={`sub-${s.id}`} value={s.id}>{s.name}</SelectItem>
                                      ))}
                                    </SelectGroup>
                                  )}
                                  {pickDrivers.length > 0 && (
                                    <SelectGroup>
                                      <SelectLabel className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Self-employed drivers</SelectLabel>
                                      {pickDrivers.map((p) => (
                                        <SelectItem key={`drv-${p.id}`} value={p.id}>
                                          {p.name}{p.driver_id ? ` (${p.driver_id})` : ""}
                                        </SelectItem>
                                      ))}
                                    </SelectGroup>
                                  )}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="col-span-2">
                              <Input
                                data-testid={`dsp-drivers-${idx}`}
                                type="number"
                                min="0"
                                value={entry.drivers}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  setForm((f) => {
                                    const next = [...f.dsp_days];
                                    next[idx] = { ...next[idx], drivers: v === "" ? 0 : Number(v) };
                                    return { ...f, dsp_days: next };
                                  });
                                }}
                                className="rounded-sm border-slate-300 tabular-nums text-right h-9"
                              />
                            </div>
                            <div className="col-span-1 text-right">
                              <button
                                type="button"
                                data-testid={`dsp-remove-${idx}`}
                                className="text-slate-400 hover:text-red-600"
                                onClick={() => setForm((f) => ({ ...f, dsp_days: f.dsp_days.filter((_, i) => i !== idx) }))}
                              >
                                ×
                              </button>
                            </div>
                          </div>
                          );
                        })}
                      </div>
                      <div className="px-3 py-2 border-t border-slate-100 bg-slate-50">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="rounded-sm"
                          data-testid="dsp-add-entry"
                          onClick={() => {
                            const last = form.dsp_days[form.dsp_days.length - 1];
                            const defaultDate = last?.date || allWeekDates[0] || form.date;
                            setForm((f) => ({
                              ...f,
                              dsp_days: [...f.dsp_days, { date: defaultDate, site_id: "", subcontractor_id: "", drivers: 0 }],
                            }));
                          }}
                        >
                          + Add entry
                        </Button>
                        {!form.client_id && (
                          <span className="ml-3 text-[11px] text-amber-700">
                            Pick a Client at the top to enable the Site dropdown.
                          </span>
                        )}
                      </div>
                    </div>
                    );
                  })()}
                </div>
              </>
            )}
            <div className="md:col-span-2">
              <Label className="text-xs text-slate-600">Status</Label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="job-status" className="mt-1 rounded-sm border-slate-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((s) => (<SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs text-slate-600">Vehicle type</Label>
              <SearchableCombo
                testid="job-vehicle-type"
                placeholder="Type to search or add a vehicle…"
                value={form.dsp_vehicle_type || ""}
                onChange={(v) => set("dsp_vehicle_type", v)}
                options={DSP_VEHICLE_TYPES}
                freeform
              />
              <p className="text-[11px] text-slate-500 mt-1">Not in the list? Just type it and press Enter.</p>
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs text-slate-600">Description</Label>
              <Textarea data-testid="job-description" rows={3} value={form.description || ""}
                onChange={(e) => set("description", e.target.value)}
                placeholder="Short description of what needs collecting/delivering."
                className="mt-1 rounded-sm border-slate-300" />
            </div>
          </div>
        </Card>

        {form.service_type !== "DSP" && (
          <Card className="rounded-sm border border-slate-200 shadow-none p-6">
            <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold mb-4">Pricing</div>
            <div className="space-y-3">
              <Field label="Charge to customer (£)" testid="job-charge" type="number" step="0.01" value={form.charge} onChange={(v) => set("charge", v)} />
            </div>
          </Card>
        )}
      </div>

      {form.service_type !== "DSP" && (
        <>
          {/* Route */}
          <Card className="rounded-sm border border-slate-200 shadow-none p-6">
            <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold mb-4">Route</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-xs text-slate-600">Collection address</Label>
                  {sites.length > 0 && (
                    <Select
                      value="__pick"
                      onValueChange={(v) => {
                        if (v === "__pick") return;
                        const s = sites.find((x) => x.id === v);
                        if (s) {
                          const addr = [s.name, s.address_line1, s.address_line2, s.city, s.postcode].filter(Boolean).join(", ");
                          set("collection_address", addr);
                        }
                      }}
                    >
                      <SelectTrigger data-testid="job-collection-picker" className="h-7 w-44 rounded-sm border-slate-300 text-[11px]">
                        <SelectValue placeholder="Pick from sites" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__pick">Pick from sites</SelectItem>
                        {sites.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
                <Textarea data-testid="job-collection" rows={3} value={form.collection_address}
                  onChange={(e) => set("collection_address", e.target.value)}
                  placeholder="Pick a site above or type manually"
                  className="mt-1 rounded-sm border-slate-300" />
                <Field label="Collection time" testid="job-collection-time" value={form.collection_time}
                  onChange={(v) => set("collection_time", v)} placeholder="e.g. Before 10am" />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-xs text-slate-600">Drop-off address</Label>
                  {sites.length > 0 && (
                    <Select
                      value="__pick"
                      onValueChange={(v) => {
                        if (v === "__pick") return;
                        const s = sites.find((x) => x.id === v);
                        if (!s) return;
                        const siteDrops = (s.routes || []).filter((r) => (r?.dropoff_address || "").trim());
                        if (siteDrops.length > 0) {
                          setForm((f) => ({
                            ...f,
                            dropoff_address: siteDrops[0].dropoff_address,
                            extra_dropoffs: siteDrops.slice(1).map((r) => ({ address: r.dropoff_address, time: "" })),
                            distance_miles: (!f.distance_miles || Number(f.distance_miles) === 0) ? (s.miles || f.distance_miles) : f.distance_miles,
                          }));
                          toast.success(`${siteDrops.length} drop${siteDrops.length === 1 ? "" : "s"} auto-filled from ${s.name}`);
                        } else {
                          const addr = [s.name, s.address_line1, s.address_line2, s.city, s.postcode].filter(Boolean).join(", ");
                          set("dropoff_address", addr);
                        }
                      }}
                    >
                      <SelectTrigger data-testid="job-dropoff-picker" className="h-7 w-44 rounded-sm border-slate-300 text-[11px]">
                        <SelectValue placeholder="Pick from sites" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__pick">Pick from sites</SelectItem>
                        {sites.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
                <Textarea data-testid="job-dropoff" rows={3} value={form.dropoff_address}
                  onChange={(e) => set("dropoff_address", e.target.value)}
                  placeholder="Pick a site above or type manually"
                  className="mt-1 rounded-sm border-slate-300" />
                <Field label="Drop-off time" testid="job-dropoff-time" value={form.dropoff_time}
                  onChange={(v) => set("dropoff_time", v)} placeholder="e.g. By 2pm" />
              </div>

              {/* Additional drop-offs — one pickup, multiple drops on the same route. */}
              {form.service_type !== "DSP" && (
                <div className="mt-4 space-y-3" data-testid="extra-dropoffs-block">
                  {(form.extra_dropoffs || []).map((d, idx) => (
                    <div key={idx} className="rounded-sm border border-slate-200 bg-slate-50/60 p-3" data-testid={`extra-dropoff-${idx}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-700">
                          Drop-off #{idx + 2}
                        </div>
                        <button
                          type="button"
                          data-testid={`extra-dropoff-remove-${idx}`}
                          onClick={() => set("extra_dropoffs", form.extra_dropoffs.filter((_, i) => i !== idx))}
                          className="text-xs text-red-600 hover:text-red-700 inline-flex items-center gap-1"
                        >
                          <Trash2 className="h-3.5 w-3.5" /> Remove
                        </button>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div className="md:col-span-2">
                          <Label className="text-xs text-slate-600">Drop-off address</Label>
                          <Textarea
                            data-testid={`extra-dropoff-address-${idx}`}
                            rows={2}
                            value={d.address || ""}
                            onChange={(e) => {
                              const next = [...form.extra_dropoffs];
                              next[idx] = { ...next[idx], address: e.target.value };
                              set("extra_dropoffs", next);
                            }}
                            placeholder="Full address"
                            className="mt-1 rounded-sm border-slate-300 text-sm"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-600">Drop-off time</Label>
                          <Input
                            data-testid={`extra-dropoff-time-${idx}`}
                            value={d.time || ""}
                            onChange={(e) => {
                              const next = [...form.extra_dropoffs];
                              next[idx] = { ...next[idx], time: e.target.value };
                              set("extra_dropoffs", next);
                            }}
                            placeholder="e.g. By 3pm"
                            className="mt-1 rounded-sm border-slate-300 text-sm"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    data-testid="extra-dropoff-add"
                    onClick={() => set("extra_dropoffs", [...(form.extra_dropoffs || []), { address: "", time: "" }])}
                    className="rounded-sm"
                  >
                    <Plus className="h-3.5 w-3.5 mr-1.5" />
                    Add another drop-off
                  </Button>
                </div>
              )}
              <Field label="Distance (miles)" testid="job-distance" type="number" step="0.1" value={form.distance_miles} onChange={(v) => set("distance_miles", v)} />
              <Field label="Vehicle reg" testid="job-vehicle" value={form.vehicle_reg} onChange={(v) => set("vehicle_reg", v.toUpperCase())} />
            </div>

            {/* Extra routes — for any non-DSP job with two or more legs.
                Button stays visible so you can add as many stops as needed. */}
            {form.service_type !== "DSP" && (
              <div className="mt-6 pt-5 border-t border-slate-200 space-y-4" data-testid="extra-routes-block">
                {(form.extra_routes || []).map((r, idx) => (
                  <div key={idx} className="rounded-sm border border-orange-200 bg-orange-50/40 p-4" data-testid={`extra-route-${idx}`}>
                    <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
                      <div className="flex items-center gap-3">
                        <div className="text-xs uppercase tracking-[0.18em] font-semibold text-orange-700">Route {idx + 2}</div>
                        <div className="flex items-center gap-1.5">
                          <Label className="text-[10.5px] uppercase tracking-wider text-slate-500">Date</Label>
                          <Input
                            data-testid={`extra-route-date-${idx}`}
                            type="date"
                            value={r.date || ""}
                            onChange={(e) => {
                              const next = [...form.extra_routes];
                              next[idx] = { ...next[idx], date: e.target.value };
                              set("extra_routes", next);
                            }}
                            className="h-7 w-40 rounded-sm border-slate-300 text-xs"
                          />
                        </div>
                      </div>
                      <button
                        type="button"
                        data-testid={`extra-route-remove-${idx}`}
                        onClick={() => set("extra_routes", form.extra_routes.filter((_, i) => i !== idx))}
                        className="text-xs text-red-600 hover:text-red-700 inline-flex items-center gap-1"
                      >
                        <Trash2 className="h-3.5 w-3.5" /> Remove
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <div className="flex items-center justify-between">
                          <Label className="text-xs text-slate-600">Collection address</Label>
                          {sites.length > 0 && (
                            <Select
                              value="__pick"
                              onValueChange={(v) => {
                                if (v === "__pick") return;
                                const s = sites.find((x) => x.id === v);
                                if (s) {
                                  const addr = [s.name, s.address_line1, s.address_line2, s.city, s.postcode].filter(Boolean).join(", ");
                                  const next = [...form.extra_routes];
                                  next[idx] = { ...next[idx], collection_address: addr };
                                  set("extra_routes", next);
                                }
                              }}
                            >
                              <SelectTrigger data-testid={`extra-route-collection-picker-${idx}`} className="h-7 w-44 rounded-sm border-slate-300 text-[11px]">
                                <SelectValue placeholder="Pick from sites" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__pick">Pick from sites</SelectItem>
                                {sites.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                        <Textarea
                          data-testid={`extra-route-collection-${idx}`}
                          rows={3}
                          value={r.collection_address || ""}
                          onChange={(e) => {
                            const next = [...form.extra_routes];
                            next[idx] = { ...next[idx], collection_address: e.target.value };
                            set("extra_routes", next);
                          }}
                          placeholder="Pick a site above or type manually"
                          className="mt-1 rounded-sm border-slate-300"
                        />
                        <Field
                          label="Collection time"
                          testid={`extra-route-collection-time-${idx}`}
                          value={r.collection_time || ""}
                          onChange={(v) => {
                            const next = [...form.extra_routes];
                            next[idx] = { ...next[idx], collection_time: v };
                            set("extra_routes", next);
                          }}
                          placeholder="e.g. Before 10am"
                        />
                      </div>
                      <div>
                        <div className="flex items-center justify-between">
                          <Label className="text-xs text-slate-600">Drop-off address</Label>
                          {sites.length > 0 && (
                            <Select
                              value="__pick"
                              onValueChange={(v) => {
                                if (v === "__pick") return;
                                const s = sites.find((x) => x.id === v);
                                if (!s) return;
                                const siteDrops = (s.routes || []).filter((rr) => (rr?.dropoff_address || "").trim());
                                const next = [...form.extra_routes];
                                if (siteDrops.length > 0) {
                                  next[idx] = {
                                    ...next[idx],
                                    dropoff_address: siteDrops[0].dropoff_address,
                                    extra_dropoffs: siteDrops.slice(1).map((rr) => ({ address: rr.dropoff_address, time: "" })),
                                  };
                                  set("extra_routes", next);
                                  toast.success(`${siteDrops.length} drop${siteDrops.length === 1 ? "" : "s"} auto-filled from ${s.name}`);
                                } else {
                                  const addr = [s.name, s.address_line1, s.address_line2, s.city, s.postcode].filter(Boolean).join(", ");
                                  next[idx] = { ...next[idx], dropoff_address: addr };
                                  set("extra_routes", next);
                                }
                              }}
                            >
                              <SelectTrigger data-testid={`extra-route-dropoff-picker-${idx}`} className="h-7 w-44 rounded-sm border-slate-300 text-[11px]">
                                <SelectValue placeholder="Pick from sites" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__pick">Pick from sites</SelectItem>
                                {sites.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                        <Textarea
                          data-testid={`extra-route-dropoff-${idx}`}
                          rows={3}
                          value={r.dropoff_address || ""}
                          onChange={(e) => {
                            const next = [...form.extra_routes];
                            next[idx] = { ...next[idx], dropoff_address: e.target.value };
                            set("extra_routes", next);
                          }}
                          placeholder="Pick a site above or type manually"
                          className="mt-1 rounded-sm border-slate-300"
                        />
                        <Field
                          label="Drop-off time"
                          testid={`extra-route-dropoff-time-${idx}`}
                          value={r.dropoff_time || ""}
                          onChange={(v) => {
                            const next = [...form.extra_routes];
                            next[idx] = { ...next[idx], dropoff_time: v };
                            set("extra_routes", next);
                          }}
                          placeholder="e.g. By 2pm"
                        />
                      </div>
                    </div>
                    {/* Per-route additional drop-offs — matches Route 1's "+ Add another drop-off"
                        so a same-day multi-leg job can have multiple drops per leg. */}
                    <div className="mt-4 space-y-3" data-testid={`extra-route-dropoffs-${idx}`}>
                      {(r.extra_dropoffs || []).map((d, dIdx) => (
                        <div key={dIdx} className="rounded-sm border border-orange-200 bg-white p-3" data-testid={`extra-route-${idx}-drop-${dIdx}`}>
                          <div className="flex items-center justify-between mb-2">
                            <Label className="text-[10.5px] uppercase tracking-[0.18em] font-semibold text-slate-500">Drop-off #{dIdx + 2}</Label>
                            <button
                              type="button"
                              data-testid={`extra-route-${idx}-drop-remove-${dIdx}`}
                              onClick={() => {
                                const next = [...form.extra_routes];
                                next[idx] = { ...next[idx], extra_dropoffs: (next[idx].extra_dropoffs || []).filter((_, i) => i !== dIdx) };
                                set("extra_routes", next);
                              }}
                              className="text-xs text-red-600 hover:text-red-700 inline-flex items-center gap-1"
                            >
                              <Trash2 className="h-3.5 w-3.5" /> Remove
                            </button>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                              <Label className="text-xs text-slate-600">Drop-off address</Label>
                              <Textarea
                                data-testid={`extra-route-${idx}-drop-address-${dIdx}`}
                                rows={2}
                                value={d.address || ""}
                                onChange={(e) => {
                                  const next = [...form.extra_routes];
                                  const drops = [...(next[idx].extra_dropoffs || [])];
                                  drops[dIdx] = { ...drops[dIdx], address: e.target.value };
                                  next[idx] = { ...next[idx], extra_dropoffs: drops };
                                  set("extra_routes", next);
                                }}
                                className="mt-1 rounded-sm border-slate-300"
                              />
                            </div>
                            <div>
                              <Label className="text-xs text-slate-600">Drop-off time</Label>
                              <Input
                                data-testid={`extra-route-${idx}-drop-time-${dIdx}`}
                                value={d.time || ""}
                                onChange={(e) => {
                                  const next = [...form.extra_routes];
                                  const drops = [...(next[idx].extra_dropoffs || [])];
                                  drops[dIdx] = { ...drops[dIdx], time: e.target.value };
                                  next[idx] = { ...next[idx], extra_dropoffs: drops };
                                  set("extra_routes", next);
                                }}
                                placeholder="e.g. By 3pm"
                                className="mt-1 rounded-sm border-slate-300"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        data-testid={`extra-route-drop-add-${idx}`}
                        onClick={() => {
                          const next = [...form.extra_routes];
                          next[idx] = { ...next[idx], extra_dropoffs: [...(next[idx].extra_dropoffs || []), { address: "", time: "" }] };
                          set("extra_routes", next);
                        }}
                        className="rounded-sm"
                      >
                        <Plus className="h-3.5 w-3.5 mr-1.5" />
                        Add another drop-off
                      </Button>
                    </div>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  data-testid="extra-route-add"
                  onClick={() => {
                    // Default the new route's date to the day after the last existing
                    // route (primary date + N days), so a Mon-Fri client can just tap
                    // "Add more route" four more times to cover the week.
                    const existing = form.extra_routes || [];
                    const base = existing.length > 0 ? existing[existing.length - 1].date : form.date;
                    let nextDate = "";
                    if (base) {
                      const d = new Date(base); d.setDate(d.getDate() + 1);
                      nextDate = d.toISOString().slice(0, 10);
                    }
                    // Copy the addresses/times from the most-recent route (or the primary
                    // route) so recurring same-day contracts don't need to be retyped.
                    const src = existing.length > 0 ? existing[existing.length - 1] : {
                      collection_address: form.collection_address,
                      collection_time: form.collection_time,
                      dropoff_address: form.dropoff_address,
                      dropoff_time: form.dropoff_time,
                    };
                    set("extra_routes", [...existing, {
                      date: nextDate,
                      collection_address: src.collection_address || "",
                      collection_time: src.collection_time || "",
                      dropoff_address: src.dropoff_address || "",
                      dropoff_time: src.dropoff_time || "",
                    }]);
                  }}
                  className="rounded-sm"
                >
                  <Plus className="h-3.5 w-3.5 mr-1.5" />
                  Add more route
                </Button>
              </div>
            )}
            {/* Also create on these dates — clones this job onto each date.
                Only shown when creating a fresh non-DSP job. In edit mode use
                the Repeat dialog at the top of the form instead. */}
            {!isEdit && form.service_type !== "DSP" && (
              <div className="mt-6 pt-5 border-t border-dashed border-slate-300 space-y-3" data-testid="extra-dates-block">
                <div>
                  <Label className="text-sm font-medium text-slate-900">Also create on these dates</Label>
                  <p className="text-[11px] text-slate-500 mt-0.5">
                    Same client, same collection, same drop-offs — just repeated on each date. One job is created per date.
                  </p>
                </div>
                {(form.extra_dates || []).length > 0 && (
                  <div className="space-y-2">
                    {(form.extra_dates || []).map((d, idx) => (
                      <div key={idx} className="flex items-center gap-2" data-testid={`extra-date-row-${idx}`}>
                        <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 w-16 shrink-0">Date {idx + 2}</span>
                        <Input
                          type="date"
                          data-testid={`extra-date-input-${idx}`}
                          value={d || ""}
                          onChange={(e) => {
                            const next = [...form.extra_dates];
                            next[idx] = e.target.value;
                            set("extra_dates", next);
                          }}
                          className="h-9 rounded-sm border-slate-300 max-w-[220px]"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          data-testid={`extra-date-remove-${idx}`}
                          className="h-8 text-red-600 hover:text-red-700"
                          onClick={() => set("extra_dates", form.extra_dates.filter((_, i) => i !== idx))}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  data-testid="extra-date-add"
                  onClick={() => {
                    // Default the new date to the day after the last extra date
                    // (or the primary date). Users can then adjust as needed.
                    const existing = form.extra_dates || [];
                    const base = existing.length > 0 ? existing[existing.length - 1] : form.date;
                    let nextDate = "";
                    if (base) {
                      const d = new Date(base + "T00:00:00");
                      d.setDate(d.getDate() + 1);
                      nextDate = d.toISOString().slice(0, 10);
                    }
                    set("extra_dates", [...existing, nextDate]);
                  }}
                  className="rounded-sm"
                >
                  <Plus className="h-3.5 w-3.5 mr-1.5" />
                  Add another date
                </Button>
              </div>
            )}
          </Card>

          {/* Assignment — for Same-day / non-DSP jobs, rendered right after Route
              so the ops team can pick the driver in context of the job's route. */}
          {form.service_type !== "DSP" && AssignmentCard}

          {/* Driver + Package */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="rounded-sm border border-slate-200 shadow-none p-6">
              <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold mb-4">Package</div>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label className="text-xs text-slate-600">Description</Label>
                  <Input data-testid="job-pkg-desc" value={form.package_description}
                    onChange={(e) => set("package_description", e.target.value)}
                    placeholder="e.g. 4 pallets of car parts" className="mt-1 rounded-sm border-slate-300" />
                </div>
                <Field label="Pieces" testid="job-pkg-pieces" type="number" min="1" value={form.package_pieces} onChange={(v) => set("package_pieces", v)} />
                <Field label="Weight (kg)" testid="job-pkg-weight" type="number" step="0.1" value={form.package_weight} onChange={(v) => set("package_weight", v)} />
              </div>
            </Card>
            <Card className="rounded-sm border border-slate-200 shadow-none p-6">
              <Label className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Special instructions</Label>
              <Textarea data-testid="job-instructions" rows={6} value={form.instructions} onChange={(e) => set("instructions", e.target.value)}
                className="mt-3 rounded-sm border-slate-300" placeholder="Fragile, deliver to reception, ring on arrival, etc." />
            </Card>
          </div>
        </>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {form.service_type === "DSP" && (
          <Card className="rounded-sm border border-slate-200 shadow-none p-6">
            <Label className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Special instructions</Label>
            <Textarea data-testid="job-instructions" rows={4} value={form.instructions} onChange={(e) => set("instructions", e.target.value)}
              className="mt-3 rounded-sm border-slate-300" placeholder="Notes for the run, fuel pickups, schedule changes, etc." />
          </Card>
        )}
        <Card className="rounded-sm border border-slate-200 shadow-none p-6">
          <Label className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Internal notes</Label>
          <Textarea data-testid="job-notes" rows={4} value={form.notes} onChange={(e) => set("notes", e.target.value)}
            className="mt-3 rounded-sm border-slate-300" placeholder="Not shown to driver / customer." />
        </Card>
      </div>

      {/* Job status history — read-only timeline, only meaningful for saved jobs */}
      {isEdit && (
        <JobStatusHistory events={form.status_history || []} createdAt={form.created_at} />
      )}

      {/* Photos & documents — hidden for DSP runs */}
      {form.service_type !== "DSP" && (
        <Card className="rounded-sm border border-slate-200 shadow-none p-6">
          <FileUploader entityType="job" entityId={isEdit ? id : ""} label="Photos & documents" />
        </Card>
      )}
    </div>
  );
}

function Field({ label, value, onChange, testid, type = "text", ...rest }) {
  return (
    <div>
      <Label className="text-xs text-slate-600">{label}</Label>
      <Input data-testid={testid} type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
        className="mt-1 rounded-sm border-slate-300" {...rest} />
    </div>
  );
}

// ---------- Job status history timeline ----------
// Read-only vertical list of the significant events on the job:
// created → status changes → assignment → invoiced/uninvoiced.
// Falls back gracefully for older jobs that have no `status_history` array
// stored by showing just the "created" entry from `created_at`.
const HISTORY_META = {
  created:                { label: "Job created",      icon: CircleDot,     tone: "text-slate-600" },
  assigned:               { label: "Assigned",         icon: UserCheck,     tone: "text-blue-600" },
  invoiced:               { label: "Marked invoiced",  icon: Receipt,       tone: "text-emerald-600" },
  uninvoiced:             { label: "Invoice reverted", icon: RotateCcw,     tone: "text-amber-600" },
  "status:new":           { label: "Status: New",         icon: CircleDot,   tone: "text-slate-600" },
  "status:assigned":      { label: "Status: Assigned",    icon: UserCheck,   tone: "text-blue-600" },
  "status:in_progress":   { label: "Status: In progress", icon: PlayCircle,  tone: "text-indigo-600" },
  "status:complete":      { label: "Status: Complete",    icon: CheckCircle2,tone: "text-emerald-600" },
  "status:on_hold":       { label: "Status: On hold",     icon: PauseCircle, tone: "text-amber-600" },
  "status:cancelled":     { label: "Status: Cancelled",   icon: PauseCircle, tone: "text-rose-600" },
};

function relativeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diff = Math.max(0, Date.now() - then);
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hour${h === 1 ? "" : "s"} ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d} day${d === 1 ? "" : "s"} ago`;
  return new Date(iso).toLocaleDateString("en-GB");
}

function formatWhen(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function JobStatusHistory({ events, createdAt }) {
  // Older jobs never had a history array — synthesise a single "created" entry
  // so the timeline is still useful.
  let list = Array.isArray(events) && events.length > 0 ? events : [];
  if (list.length === 0 && createdAt) {
    list = [{ kind: "created", at: createdAt, by_name: "" }];
  }
  if (list.length === 0) return null;

  // Show newest first so the most recent event lands at the top.
  const ordered = [...list].sort((a, b) => (b.at || "").localeCompare(a.at || ""));

  return (
    <Card className="rounded-sm border border-slate-200 shadow-none p-6" data-testid="job-status-history">
      <div className="flex items-center gap-2 mb-4">
        <History className="h-4 w-4 text-slate-500" />
        <h3 className="text-sm font-semibold text-slate-800 tracking-tight">Job status history</h3>
        <span className="text-[11px] text-slate-500">· {ordered.length} event{ordered.length === 1 ? "" : "s"}</span>
      </div>
      <ol className="relative border-l border-slate-200 ml-1.5">
        {ordered.map((ev, idx) => {
          const meta = HISTORY_META[ev.kind] || { label: ev.kind, icon: CircleDot, tone: "text-slate-500" };
          const Icon = meta.icon;
          return (
            <li
              key={`${ev.at}-${idx}`}
              data-testid={`job-history-${idx}`}
              className="ml-4 pb-4 last:pb-0"
            >
              <span className={`absolute -left-[9px] flex h-4 w-4 items-center justify-center rounded-full bg-white ring-4 ring-white ${meta.tone}`}>
                <Icon className="h-3.5 w-3.5" />
              </span>
              <div className="flex flex-wrap items-baseline gap-x-2">
                <span className="text-sm font-medium text-slate-800">{meta.label}</span>
                <span className="text-[11px] text-slate-500" title={formatWhen(ev.at)}>
                  · {relativeAgo(ev.at)}
                </span>
              </div>
              <div className="text-[12px] text-slate-500 mt-0.5">
                {formatWhen(ev.at)}{ev.by_name ? ` · by ${ev.by_name}` : ""}
                {ev.assignee ? <span className="ml-1 text-slate-600">→ {ev.assignee}</span> : null}
              </div>
            </li>
          );
        })}
      </ol>
    </Card>
  );
}

// Type-to-search site picker used inside the DSP day grid.
// Matches the Shadcn `Select` chrome (h-9, rounded-sm, border-slate-300) so it
// drops in next to the other selects without breaking the row layout.
function SiteCombobox({ value, onChange, sites, disabled, placeholder, testid }) {
  const [open, setOpen] = useState(false);
  const selected = sites.find((s) => s.id === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          data-testid={testid}
          className="flex h-9 w-full items-center justify-between rounded-sm border border-slate-300 bg-white px-3 text-sm text-slate-900 disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
        >
          <span className={`truncate text-left ${selected ? "" : "text-slate-500"}`}>
            {selected ? selected.name : (placeholder || "— Not specified —")}
          </span>
          <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-slate-400 ml-2" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0 rounded-sm" align="start">
        <Command>
          <CommandInput placeholder="Type to search sites..." className="h-9" />
          <CommandList>
            <CommandEmpty>No sites match.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="__not-specified__"
                onSelect={() => { onChange(""); setOpen(false); }}
              >
                <Check className={`mr-2 h-4 w-4 ${value ? "opacity-0" : "opacity-100"}`} />
                <span className="text-slate-500">— Not specified —</span>
              </CommandItem>
              {sites.map((s) => (
                <CommandItem
                  key={s.id}
                  value={s.name}
                  onSelect={() => { onChange(s.id); setOpen(false); }}
                >
                  <Check className={`mr-2 h-4 w-4 ${value === s.id ? "opacity-100" : "opacity-0"}`} />
                  <span className="truncate">{s.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}



/**
 * Type-to-search combobox with optional free-form entry.
 *
 * Props:
 *  - value / onChange    controlled string
 *  - options             array of { value, label } or plain strings
 *  - placeholder         text shown when nothing picked
 *  - freeform            true → pressing Enter with un-listed text accepts it
 *                        as a custom value (used for Vehicle type)
 */
function SearchableCombo({ value, onChange, options, placeholder, testid, freeform = false }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const norm = (options || []).map((o) => (typeof o === "string" ? { value: o, label: o } : o));
  const matches = q.trim()
    ? norm.filter((o) => (o.label || "").toLowerCase().includes(q.toLowerCase()))
    : norm;
  const showAsCustom = freeform && q.trim() && !norm.some((o) => (o.label || "").toLowerCase() === q.trim().toLowerCase());
  const displayLabel = value ? (norm.find((o) => o.value === value)?.label || value) : "";

  const accept = (v) => { onChange(v); setOpen(false); setQ(""); };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testid}
          className="mt-1 w-full h-10 rounded-sm border border-slate-300 bg-white text-left px-3 text-sm flex items-center justify-between hover:bg-slate-50"
        >
          <span className={displayLabel ? "text-slate-900 truncate" : "text-slate-400"}>
            {displayLabel || placeholder || "Choose…"}
          </span>
          <span className="text-slate-400 ml-2">▾</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0 rounded-sm" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={freeform ? "Type to search or add…" : "Type to search…"}
            value={q}
            onValueChange={setQ}
            onKeyDown={(e) => {
              if (e.key === "Enter" && showAsCustom) { e.preventDefault(); accept(q.trim()); }
            }}
          />
          <CommandList>
            <CommandEmpty>
              {freeform ? <span className="text-slate-500 text-xs">Press Enter to use “{q}”</span> : "No matches"}
            </CommandEmpty>
            <CommandGroup>
              {matches.map((o) => (
                <CommandItem key={o.value} value={o.label} onSelect={() => accept(o.value)}>
                  {o.label}
                </CommandItem>
              ))}
              {showAsCustom && (
                <CommandItem value={`__custom_${q}`} onSelect={() => accept(q.trim())}>
                  <span className="italic text-slate-500">Use “{q}”</span>
                </CommandItem>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
